import { useNavigate } from 'react-router-dom';

/**
 * Safely navigates to a URL, handling both relative paths and full URLs correctly.
 *
 * This function solves the issue where React Router's navigate() function treats
 * full URLs (like https://example.com/path) as relative paths, causing malformed
 * URLs like /current-path/https://example.com/path.
 *
 * @param url - The URL to navigate to (can be relative path, hash route, or full URL)
 * @param navigate - The navigate function from useNavigate() hook
 *
 * @example
 * ```tsx
 * import { useNavigate } from 'react-router-dom';
 * import { safeNavigateToUrl } from '../lib/navigationUtils';
 *
 * const MyComponent = () => {
 *   const navigate = useNavigate();
 *
 *   const handleRedirect = (redirectUrl: string) => {
 *     safeNavigateToUrl(redirectUrl, navigate);
 *   };
 * };
 * ```
 */
const safeNavigateToUrl = (
  url: string,
  navigate: ReturnType<typeof useNavigate>
): void => {
  if (!url) return;

  try {
    // Check if it's a full URL (contains protocol)
    const urlObj = new URL(url);
    // If it's a full URL with http/https protocol, use window.location.href for external navigation
    if (urlObj.protocol === 'http:' || urlObj.protocol === 'https:') {
      window.location.href = url;
    } else {
      // For other protocols, treat as relative path
      navigate(url);
    }
  } catch {
    // If URL parsing fails, treat it as a relative path
    navigate(url);
  }
};

export { safeNavigateToUrl };
export default safeNavigateToUrl;
